import { ArrowLeftIcon } from 'lucide-react'
import { useCallback } from 'react'

import employersEmployees from 'rails_routes/Employers/EmployeesPaths'
import { StepNav } from 'slab'

import { type EmployeeOffboardingData, type OffboardingStep, useBulkOffboarding } from './BulkOffboardingContext'

function isOffboardingDataComplete (data: EmployeeOffboardingData | undefined): boolean {
  if (!data) return false
  return !!(
    data.finalDayOfEmployment &&
    data.cobraEligible !== null &&
    data.personalEmail?.trim()
  )
}

interface OffboardingSidebarProps {
  onStepClick?: (step: OffboardingStep) => void
  onEmployeeClick?: (index: number) => void
}

export function OffboardingSidebar ({ onStepClick, onEmployeeClick }: OffboardingSidebarProps) {
  const {
    currentStep,
    currentEmployeeIndex,
    getSelectedEmployees,
    employeeOffboardingData,
  } = useBulkOffboarding()

  const selectedEmployees = getSelectedEmployees()
  const showEmployeeSubSteps = currentStep === 'details' || currentStep === 'submit'

  const isStepComplete = (step: OffboardingStep): boolean => {
    switch (step) {
      case 'select':
        return currentStep !== 'select'
      case 'details':
        return currentStep === 'submit'
      case 'submit':
        return false
      default:
        return false
    }
  }

  const isEmployeeComplete = (employee: { id: string }, _index: number): boolean => {
    if (currentStep === 'select') return false
    if (currentStep === 'submit') return true
    return isOffboardingDataComplete(employeeOffboardingData.get(employee.id))
  }

  const isEmployeeActive = (index: number): boolean => {
    return currentStep === 'details' && index === currentEmployeeIndex
  }

  const handleSelectClick = useCallback(() => {
    onStepClick?.('select')
  }, [onStepClick])

  const isEmployeeAvailable = (employee: { id: string }, index: number): boolean => {
    if (index <= currentEmployeeIndex) return true
    if (isOffboardingDataComplete(employeeOffboardingData.get(employee.id))) return true

    return selectedEmployees.slice(0, index).every(emp =>
      isOffboardingDataComplete(employeeOffboardingData.get(emp.id))
    )
  }

  const handleEmployeeClick = (index: number) => {
    if (currentStep === 'details') {
      onEmployeeClick?.(index)
    }
  }

  return (
    <div className='py-6'>
      <a
        href={employersEmployees.index()}
        className='flex items-center gap-2 text-gray-500 hover:text-gray-700 body-bold px-6 mb-6'
      >
        <ArrowLeftIcon size={16} />
        <span>Back to home</span>
      </a>

      <hr className='border-gray-200 mb-6' />

      <div className='px-6'>
        <StepNav data-testid='offboarding-step-nav'>
          <StepNav.Step
            as='button'
            active={currentStep === 'select'}
            complete={isStepComplete('select')}
            available
            name='Select employees'
            onClick={handleSelectClick}
          />
          <StepNav.Step
            as='button'
            active={currentStep === 'details'}
            complete={isStepComplete('details')}
            available={currentStep !== 'select'}
            name='Offboarding details'
          >
            {showEmployeeSubSteps && selectedEmployees.map((employee, index) => (
              <StepNav.SubStep
                key={employee.id}
                as='button'
                active={isEmployeeActive(index)}
                complete={isEmployeeComplete(employee, index)}
                available={isEmployeeAvailable(employee, index)}
                name={`${employee.first_name} ${employee.last_name}`}
                onClick={() => handleEmployeeClick(index)}
              />
            ))}
          </StepNav.Step>
          <StepNav.Step
            as='button'
            active={currentStep === 'submit'}
            complete={false}
            available={currentStep === 'submit'}
            name='Submit'
          />
        </StepNav>
      </div>
    </div>
  )
}
